/**
 * Диалоги приложения: оболочка поверх поля и её кирпичи, а под ними — окна
 * игрового экрана.
 *
 * Кирпичи (карточка, кнопки, места за столом, отсчёт с кружком, плашка связи)
 * берёт и вход в партию (`lobby.tsx`): карточка входа и окно ожидания обязаны
 * совпадать. Окон игрового экрана пять, по разделу на каждое: ожидание старта,
 * вопрос и итог ответа (одно окно, содержимое сменяется на месте), падение,
 * победа.
 *
 * Модуль ничего не решает. Что и когда показывать, знает `game.tsx`, а правила
 * и сроки — сервер: здесь только разметка и отсчёт.
 */

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import { bridge, store, stored, useBridge, useCountdown, type Role } from './app.socket.js'
import { avatarFor } from './blocks/avatars.js'
import { PinOutline, Player, markerColor } from './blocks/pawn.markup.js'
import { PAWN_FRAME } from './blocks/pawn.js'
import { dismissEngineNotice, scaled, useEngineNotice } from './engines/index.js'
import { CellType, TurnEvent, type PublicQuestion, type RoomSeat, type Snapshot } from '../srv/protocol.js'

// === куски разметки ==========================================================

/*
 * Вход в партию и диалоги поверх поля собраны из одних и тех же деталей, и
 * держать их строками в двух файлах значит дать им разойтись на первой же
 * правке. Фон у карточки не здесь: во входе она лежит на странице и чуть
 * просвечивает, а диалог лежит на затемнённом поле и должен быть плотнее.
 */

/**
 * Оболочка карточки. Фон добавляет место применения. Рамка — это `outline`, и
 * `outline-none` на карточке её сносит: фокус карточке (`tabIndex=-1`) кольца не
 * рисует и без него, потому что `outline` задаёт стиль явно.
 */
export const CARD = 'rounded-2xl px-6 py-8 outline -outline-offset-1 outline-white/10'

/** Заголовок карточки с чертой под ним. */
export const CARD_TITLE =
  'mb-6 border-b border-white/10 pb-4 text-center text-lg font-bold tracking-wide text-white uppercase'

/** Главная кнопка шага. */
export const BUTTON =
  'flex w-full cursor-pointer justify-center rounded-full bg-indigo-700 px-3 py-2.5 text-sm font-bold tracking-wide text-white uppercase transition-colors hover:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400 disabled:cursor-default disabled:bg-white/10 disabled:text-gray-500'

/** Строка выбора в списке: вариант ответа, как поле — лёгкая заливка и тонкая рамка. */
const CHOICE =
  'group flex w-full cursor-pointer items-center rounded-lg bg-white/5 px-4 py-2.5 text-left text-sm text-gray-200 outline-1 -outline-offset-1 outline-white/10 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-white/5 disabled:hover:text-gray-200'

/** Второстепенная кнопка: контур без заливки. Рядом с главной она не спорит с ней цветом. */
export const BUTTON_OUTLINE =
  'flex shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full px-4 py-2.5 text-sm font-bold tracking-wide text-gray-300 uppercase outline-1 -outline-offset-1 outline-white/20 transition-colors enabled:hover:bg-white/5 enabled:hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400 disabled:cursor-default disabled:opacity-50'

/**
 * Подъём карточки диалога над центром: в визарде под карточкой входа стоит пагинатор
 * (`Stepper` в `lobby.tsx`: отступ 1.5rem и точки 0.375rem), и центрируется вся
 * связка, а не одна карточка. Половина этой высоты, 0.9375rem, и есть подъём: окно ожидания
 * встаёт ровно на место карточки входа, в которую вошли.
 */
const LIFT_FOR_STEPPER = '-top-[0.9375rem]'

/**
 * Заблокированное содержимое при потере связи: действия, которым нужен сервер, всё
 * равно уходят в пустоту, и живая кнопка только обманывает. `inert` снимает с блока
 * фокус, щелчки и вспомогательные технологии разом; затемнение показывает, что
 * блок не отвечает. React 18 не знает булевый `inert`, поэтому атрибут идёт строкой.
 */
export const lockedWhen = (offline: boolean) =>
  offline ? ({ inert: '', 'aria-disabled': true } as const) : {}

/** Затемнение заблокированного блока; ставится в `className` рядом с `lockedWhen`. */
export const dimWhen = (offline: boolean) => `transition-opacity ${offline ? 'opacity-50' : ''}`

// === оболочка ================================================================

export type DialogRect = { width: number; height: number; left: number; top: number }
export type DialogPoint = { x: number; y: number }

/**
 * Тень диалога поверх поля: широкая мягкая отбрасывает карточку от затемнённой
 * сцены, узкая плотная — собирает край. Одна на все диалоги, чтобы окна не
 * расходились по глубине.
 */
const DIALOG_SHADOW = 'shadow-[0_32px_80px_-12px_rgba(0,0,0,0.85),0_8px_24px_-4px_rgba(0,0,0,0.6)]'

/*
 * Откуда раскрывается и куда схлопывается окно хода: из клетки с игроком или из своего центра
 * (по центру экрана). Выбор клиентский: хранится в браузере и действует на лету, меняется в сайдбаре.
 */
export type ZoomOrigin = 'cell' | 'center'
const ZOOM_KEY = 'tolk.dialogZoom'
let zoomOrigin: ZoomOrigin = stored(ZOOM_KEY) === 'center' ? 'center' : 'cell'
const zoomListeners = new Set<() => void>()
export function setZoomOrigin(next: ZoomOrigin): void {
  zoomOrigin = next
  store(ZOOM_KEY, next)
  for (const listener of zoomListeners) listener()
}
export function useZoomOrigin(): ZoomOrigin {
  return useSyncExternalStore(
    (listener) => {
      zoomListeners.add(listener)
      return () => zoomListeners.delete(listener)
    },
    () => zoomOrigin,
  )
}

/** Сколько играет zoom-out при закрытии: короче раскрытия, уход не должен задерживать игру. */
const DIALOG_EXIT_MS = 360

/**
 * Держит диалог на экране, пока он закрывается. Диалоги хода открывает состояние партии, и когда оно
 * выключается, содержимое уже пустое: хук хранит последние свойства и отдаёт их с `closing`, пока `Shell`
 * играет zoom-out, затем отпускает. Новый диалог на этом месте снимает прощание.
 */
export function useExit<T>(props: T | null): { props: T | null; closing: boolean } {
  const held = useRef<T | null>(props)
  if (props) held.current = props
  const [, redraw] = useState(0)
  const open = !!props
  useEffect(() => {
    if (open || !held.current) return
    const timer = setTimeout(() => {
      held.current = null
      redraw((n) => n + 1)
    }, scaled(DIALOG_EXIT_MS))
    return () => clearTimeout(timer)
  }, [open])
  return { props: props ?? held.current, closing: !props && !!held.current }
}

/**
 * Оболочка: затемнение на весь экран и карточка по центру игровой области.
 *
 * `shift` тот же, что у сцены, — половина открытой панели. Оверлей отдаёт карточке
 * только область справа от панели, поэтому она стоит по центру поля, а не окна. Затемнение плоское, без
 * размытия: за ним крутится сцена, а фильтр над анимацией дорог.
 */
export function Shell({
  shift = 0,
  titleId,
  focusRef,
  onDismiss,
  rect,
  onRectChange,
  zoomFrom,
  closing = false,
  joinCard = false,
  children,
}: {
  shift?: number
  titleId: string
  focusRef?: RefObject<HTMLElement | null>
  /** Закрытие без выбора: Escape и щелчок по затемнению. У диалогов с обязательным ответом его нет. */
  onDismiss?: () => void
  /** Зафиксированное положение и размер для бесшовной смены содержимого окна. */
  rect?: DialogRect | null
  onRectChange?: (rect: DialogRect) => void
  /** Начальная точка раскрытия окна в координатах экрана. */
  zoomFrom?: DialogPoint | null
  /** Диалог закрывается: окно схлопывается к `zoomFrom` и гаснет, управлять им уже нельзя. */
  closing?: boolean
  /** Размер и место карточки входа «Join the room»: так окно ожидания продолжает её на месте. */
  joinCard?: boolean
  children: ReactNode
}) {
  const card = useRef<HTMLDivElement>(null)
  const veil = useRef<HTMLDivElement>(null)
  // Из центра окна — когда выбрано «center»: сдвиг нулевой, окно раскрывается на месте.
  const fromCell = useZoomOrigin() === 'cell'
  // Связи нет: всё содержимое окна заперто, пока она не вернётся (см. `lockedWhen`).
  const offline = !useBridge().connected
  // Фокус внутрь: диалог модальный, и клавиатура должна начинать с него, а не с поля под ним.
  useEffect(() => {
    ;(focusRef?.current ?? card.current)?.focus({ preventScroll: true })
  }, [focusRef])
  useEffect(() => {
    if (!onDismiss || closing) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDismiss, closing])
  useLayoutEffect(() => {
    if (!onRectChange || !card.current) return
    // Снимаем исходный размер до zoom-in: во время WAAPI-анимации
    // getBoundingClientRect отражает временный масштаб окна.
    const { width, height, left, top } = card.current.getBoundingClientRect()
    onRectChange({ width, height, left, top })
  }, [onRectChange])
  // Раскрытие играет один раз за жизнь окна. Перерисовка игры (обрыв связи, возврат, отсчёт) даёт
  // `zoomFrom` новую ссылку, и анимация, привязанная к ней, проигрывалась бы заново поверх готового окна.
  const entered = useRef<Animation | null>(null)
  useLayoutEffect(() => {
    const node = card.current
    if (entered.current || !node || !zoomFrom) return
    const rect = node.getBoundingClientRect()
    const dx = fromCell ? zoomFrom.x - (rect.left + rect.width / 2) : 0
    const dy = fromCell ? zoomFrom.y - (rect.top + rect.height / 2) : 0
    entered.current = node.animate(
      [
        { opacity: 0.35, transform: `translate(${dx}px, ${dy}px) scale(0.12)` },
        { opacity: 1, transform: 'translate(0, 0) scale(1)' },
      ],
      { duration: scaled(620), easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    )
  }, [zoomFrom])
  useLayoutEffect(
    () => () => {
      entered.current?.cancel()
      entered.current = null
    },
    [],
  )
  // Закрытие — раскрытие наоборот: окно уходит в ту же точку, затемнение гаснет вместе с ним.
  useLayoutEffect(() => {
    const node = card.current
    const cover = veil.current
    if (!closing || !node || !cover) return
    const rect = node.getBoundingClientRect()
    const dx = zoomFrom && fromCell ? zoomFrom.x - (rect.left + rect.width / 2) : 0
    const dy = zoomFrom && fromCell ? zoomFrom.y - (rect.top + rect.height / 2) : 0
    const away = node.animate(
      [
        { opacity: 1, transform: 'translate(0, 0) scale(1)' },
        { opacity: 0, transform: `translate(${dx}px, ${dy}px) scale(0.12)` },
      ],
      { duration: scaled(DIALOG_EXIT_MS), easing: 'cubic-bezier(0.7, 0, 0.84, 0)', fill: 'forwards' },
    )
    const fade = cover.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: scaled(DIALOG_EXIT_MS),
      easing: 'ease-in',
      fill: 'forwards',
    })
    return () => {
      away.cancel()
      fade.cancel()
    }
  }, [closing]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={veil}
      className="fixed inset-y-0 right-0 z-[15] flex animate-fade-in overflow-auto bg-deep/80 px-6 py-6 motion-reduce:animate-none"
      // Оверлей занимает только область справа от панели (`shift` — её половина):
      // панель остаётся доступной, а карточка стоит по центру поля. Слой выше сцены
      // (z-10), но ниже ручки панели (z-20), иначе ручку не нажать.
      style={{ left: shift * 2, pointerEvents: closing ? 'none' : undefined }}
      onClick={(e) => {
        if (onDismiss && e.target === e.currentTarget) onDismiss()
      }}
    >
      <ConnectionNote className="absolute top-4 left-1/2 -translate-x-1/2" />
      {/* `m-auto`, а не центрирование у родителя: высокая карточка на низком экране прокручивается, а не режется сверху. */}
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`${CARD} ${DIALOG_SHADOW} relative m-auto w-full ${joinCard ? `max-w-[23.625rem] ${LIFT_FOR_STEPPER}` : 'max-w-[26rem]'} min-w-[17rem] bg-surface/95 ${rect ? 'overflow-y-auto' : ''}`}
        // Высота зафиксированного окна — нижняя граница, а не точный размер: итог ответа
        // выше вопроса растёт вниз без полосы прокрутки, которая на миг сужала бы текст.
        // Полоса остаётся лишь у окна, не влезающего в экран.
        style={
          rect
            ? {
                position: 'fixed',
                left: rect.left,
                top: rect.top,
                width: rect.width,
                minHeight: rect.height,
                maxHeight: `calc(100dvh - ${rect.top}px - 1.5rem)`,
                margin: 0,
              }
            : undefined
        }
      >
        <div {...lockedWhen(offline)} className={dimWhen(offline)}>
          {children}
        </div>
      </div>
    </div>
  )
}

/**
 * Отсчёт в диалоге: маленький кружок в правом верхнем углу карточки, см. `CountdownRing`.
 * Угол один у всех диалогов и не зависит от содержимого: глаз находит время там же, где
 * в прошлый раз, а строки текста из-за него не сдвигаются. Заголовки диалогов с отсчётом
 * (`TITLE_WITH_TIMER`) оставляют под кружок поле, чтобы длинное название не лезло под него.
 */
function Timer({ left, total, hurryMs = 0 }: { left: number | null; total: number; hurryMs?: number }) {
  return (
    <div className="absolute top-[1.875rem] right-6">
      <CountdownRing left={left} total={total} hurryMs={hurryMs} size={32} />
    </div>
  )
}

/** Заголовок с запасом под кружок отсчёта в углу: слева такой же, чтобы текст остался по центру. */
const TITLE_WITH_TIMER = `${CARD_TITLE} px-10`

// === места за столом =========================================================

/**
 * Размер фишек в ряду мест по ширине ряда.
 *
 * Четыре фишки по 79 занимают 316 единиц, и на телефоне (карточка 250-300) ряд
 * ломался: три фишки в строке и одна отдельно. Фишка рисуется вектором и тянется
 * без потерь, поэтому не переносим, а сжимаем — до `min`, ниже которого бейдж с
 * именем уже не прочесть и перенос честнее.
 */

/** Ширина фишки при аватарке 45 px: 45 · 1200 / 680 ≈ 79. */
const SEAT_SIZE = 79

/** Высота берётся из кадра маркера, чтобы пропорции не расходились с полем. */
export const seatHeight = (width: number): number => (width * PAWN_FRAME.h) / PAWN_FRAME.w

/** Фишка за столом или в форме выбора: один размер, цвет и запас под имя. */
export function SeatPlayer({
  name,
  pawn,
  avatar,
  seat = 0,
  size = SEAT_SIZE,
}: RoomSeat & { seat?: number; size?: number }) {
  return (
    <div className="relative" style={{ width: size, height: seatHeight(size) }}>
      <Player avatar={avatarFor(avatar, seat)} color={pawn} name={name} size={size} x={0} y={0} />
    </div>
  )
}

/** Свободное место: пассивный пунктирный силуэт, войти можно кнопкой под столом. */
export function FreeSeat({ size, label }: { size: number; label?: string }) {
  return (
    <div className="flex flex-col items-center text-white/20" style={{ width: size }}>
      <PinOutline size={size} />
      {label && <span className="mt-1 text-[11px] text-gray-600">{label}</span>}
    </div>
  )
}

/** Зазор между фишками в ряду, пикселей: `gap-x-1`. */
const GAP = 4

export function useSeatSize(count: number, max = SEAT_SIZE, min = 48) {
  // Колбэк-ref, а не `useRef`: ряд появляется не сразу (шаг визарда, окно), и
  // эффект должен проснуться, когда он встал в разметку, а не только при монтировании.
  const [el, setEl] = useState<HTMLUListElement | null>(null)
  const [size, setSize] = useState(max)

  useLayoutEffect(() => {
    if (!el) return
    const fit = () => {
      const n = Math.max(1, count)
      const fitted = Math.floor((el.clientWidth - (n - 1) * GAP) / n)
      setSize(Math.max(min, Math.min(max, fitted)))
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    return () => observer.disconnect()
  }, [el, count, max, min])

  return { ref: setEl, size }
}

// === кружок отсчёта ==========================================================

/**
 * Обратный отсчёт кружком: секунды в центре, а убывающая дуга по обводке показывает,
 * сколько осталось от всего срока. Один вид на все отсчёты в игре — вопрос,
 * уведомление, автозапуск, ход у кубика, — чтобы время везде читалось одинаково.
 *
 * Дуга убывает по часовой стрелке и кончается наверху. Двигает её только CSS:
 * линейный оборот полукольца на весь срок (`transform`) и погасание второй
 * половины на середине (`opacity`). Обе анимации идут на композиторе, поэтому
 * между тиками таймера и под нагрузкой страницы дуга не дёргается. Тик раз в
 * 200 мс обновляет лишь цифру и следит, не разошлись ли часы: если да (пришёл
 * новый срок), анимация запускается заново с нужного места.
 *
 * Как собрана дуга. Кольцо с маской показывает ровно половину (полуплоскость), и
 * поворот элемента поворачивает эту половину: получается полукольцо
 * [A, A+180°], где A растёт от 0 до 360° за срок. Остаток — дуга [A, 360°]:
 *   A < 180°: левая половина целиком + полукольцо, обрезанное правой половиной;
 *   A ≥ 180°: полукольцо, обрезанное левой половиной.
 * Обрезка — неподвижный `clip-path` на родителе, вращается только дочерний элемент.
 */
const HALF_RIGHT = 'linear-gradient(to right, transparent 50%, #000 50%)'
const HALF_LEFT = 'linear-gradient(to left, transparent 50%, #000 50%)'

const masked = (mask: string): CSSProperties => ({ maskImage: mask, WebkitMaskImage: mask })

export function CountdownRing({
  left,
  total,
  hurryMs = 0,
  size = 64,
  className = '',
}: {
  /** Сколько осталось, мс. `null` — отсчёт ещё не пошёл: кружок полный, без цифры. */
  left: number | null
  /** Весь срок, мс: от него считается доля дуги. */
  total: number
  /** Когда уходить в акцент: последние `hurryMs` мс. */
  hurryMs?: number
  size?: number
  className?: string
}) {
  const hurry = left !== null && left <= hurryMs
  const stroke = Math.max(3, Math.round(size / 16))
  const color = hurry ? 'border-accent' : 'border-indigo-400'

  /*
   * Опорная точка анимации: момент конца срока по часам браузера и сдвиг, с
   * которого анимацию надо начать, чтобы она шла в такт. Пока новое значение
   * отсчёта укладывается в запомненный конец, анимация не трогается, и
   * переписанная разметка её не перезапускает. Смена точки перезапускает анимацию ключом:
   * у идущей анимации сдвиг задержки лишь передвигает её, но не начинает заново.
   */
  const [anchor, setAnchor] = useState<{ end: number; elapsed: number; id: number } | null>(null)
  useEffect(() => {
    if (left === null) {
      setAnchor(null)
      return
    }
    const end = performance.now() + left
    if (anchor === null || Math.abs(end - anchor.end) > 400)
      setAnchor({ end, elapsed: total - left, id: (anchor?.id ?? 0) + 1 })
  }, [left, total, anchor])

  const run = anchor !== null && total > 0
  const timing = (name: string, extra = ''): CSSProperties => ({
    animation: `${name} ${total}ms linear ${run ? -anchor.elapsed : 0}ms both ${extra}`,
    animationPlayState: run ? 'running' : 'paused',
  })
  const ring = `absolute inset-0 rounded-full border-solid ${color}`
  const half = (clip: string): CSSProperties => ({ clipPath: clip })

  return (
    <div
      role="timer"
      aria-label="Time left"
      className={`relative inline-grid place-items-center ${className}`}
      style={{ width: size, height: size }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-full border-solid border-white/15 bg-white/5"
        style={{ borderWidth: stroke }}
      />
      {/* Первая половина срока: левая половина кольца стоит на месте и гаснет на середине. */}
      <div
        key={`l${anchor?.id}`}
        aria-hidden="true"
        className="absolute inset-0"
        style={{ ...timing('ring-first-half'), ...half('inset(0 50% 0 0)') }}
      >
        <div className={ring} style={{ borderWidth: stroke, ...masked(HALF_LEFT) }} />
      </div>
      {/* Полукольцо, обрезанное правой половиной: рисует дугу [A, 180°]; гаснет вместе с левой. */}
      <div
        key={`r${anchor?.id}`}
        aria-hidden="true"
        className="absolute inset-0"
        style={{ ...timing('ring-first-half'), ...half('inset(0 0 0 50%)') }}
      >
        <div className="absolute inset-0" style={timing('ring-spin')}>
          <div className={ring} style={{ borderWidth: stroke, ...masked(HALF_RIGHT) }} />
        </div>
      </div>
      {/* То же полукольцо, обрезанное левой половиной: рисует дугу [A, 360°] во второй половине срока. */}
      <div
        key={`s${anchor?.id}`}
        aria-hidden="true"
        className="absolute inset-0"
        style={half('inset(0 50% 0 0)')}
      >
        <div className="absolute inset-0" style={timing('ring-spin')}>
          <div className={ring} style={{ borderWidth: stroke, ...masked(HALF_RIGHT) }} />
        </div>
      </div>
      <span
        className={`relative font-bold tabular-nums ${hurry ? 'text-accent' : 'text-white'}`}
        style={{ fontSize: size * 0.4, lineHeight: 1 }}
      >
        {left === null ? '' : Math.ceil(left / 1000)}
      </span>
    </div>
  )
}

// === плашка «связи нет» ======================================================

/** Сколько состояние плашки держится на экране, прежде чем смениться: быстрые смены читались мерцанием. */
const NOTE_DWELL_MS = 800

/**
 * Откладывает смену значения, пока прежнее не продержалось `ms`. Нажатие «Retry» проходит за доли
 * секунды через «Reconnecting…», отказ и новый отсчёт; без задержки текст плашки менялся трижды подряд.
 */
function useDwell<T>(value: T, ms: number): T {
  const [shown, setShown] = useState(value)
  const since = useRef(Date.now())
  useEffect(() => {
    if (value === shown) return
    const timer = setTimeout(
      () => {
        since.current = Date.now()
        setShown(value)
      },
      Math.max(0, since.current + ms - Date.now()),
    )
    return () => clearTimeout(timer)
  }, [value, shown, ms])
  return shown
}

/**
 * Плашка «связи нет»: одна на лобби и на диалоги поверх поля. Говорит правду о том, что делает клиент:
 * идёт попытка — «Reconnecting…», до следующей — отсчёт 5, 15, 30 секунд и «Retry now», после третьей
 * неудачи — ошибка и «Retry»; а если попыток никто не ведёт (свой выход через стенд), предлагает «Retry».
 * Смена состояний сглажена (`useDwell`). Место задаёт хозяин через `className`.
 */
export function ConnectionNote({ className = '' }: { className?: string }) {
  const s = useBridge()
  const left = useCountdown(s.retry?.at ?? null, null)
  const kind = s.connecting ? 'attempt' : s.retry ? 'wait' : s.unreachable ? 'failed' : 'off'
  const shown = useDwell(kind, NOTE_DWELL_MS)
  if (s.connected) return null
  // Отсчёт показывается, только пока он есть; иначе, как и «attempt», это попытка.
  const view = shown === 'wait' && !s.retry ? 'attempt' : shown
  // Три строки: заголовок, что происходит и кнопка (её нет, пока идёт попытка).
  const detail =
    view === 'attempt'
      ? 'Reconnecting…'
      : view === 'wait' && s.retry
        ? `Retrying in ${Math.max(0, Math.ceil((left ?? s.retry.at - Date.now()) / 1000))}s (${s.retry.attempt} of ${s.retry.of})`
        : view === 'failed'
          ? 'Could not reconnect to the server'
          : null
  return (
    <div
      // Отсчёт озвучке не нужен: «status» читал бы его каждую секунду.
      role={view === 'wait' ? undefined : 'status'}
      className={`flex flex-col items-center gap-1 rounded-2xl bg-amber-400/10 px-5 py-2 text-center text-xs text-amber-300 outline-1 -outline-offset-1 outline-amber-300/20 ${className}`}
    >
      <span>Connection lost</span>
      {detail && <span className="whitespace-nowrap tabular-nums">{detail}</span>}
      {/* «Retry» — заливка того же цвета и веса, что текст, без обводки: действие видно по плашке. */}
      {view !== 'attempt' && (
        <button
          type="button"
          onClick={() => bridge.connect()}
          className="mt-0.5 cursor-pointer whitespace-nowrap rounded-full bg-amber-300/15 px-3 py-px transition-colors hover:bg-amber-300/30 active:bg-amber-300/40 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-amber-300"
        >
          {view === 'wait' ? 'Retry now' : 'Retry'}
        </button>
      )}
    </div>
  )
}

// === плашка «движок сцены сменился» ==========================================

/** Через сколько плашка уходит сама. */
const ENGINE_NOTE_MS = 12_000

/**
 * Плашка о смене реализации сцены: отрисовка отказала и партия ушла на реализацию по умолчанию, либо Pixi подменил
 * отрисовщик. Тот же вид, что у «Connection lost»; кнопка закрывает, через `ENGINE_NOTE_MS` уходит сама.
 */
export function EngineNote({ className = '' }: { className?: string }) {
  const notice = useEngineNotice()
  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(dismissEngineNotice, ENGINE_NOTE_MS)
    return () => clearTimeout(timer)
  }, [notice])
  if (!notice) return null
  return (
    // Подложка плотная: плашка стоит над сценой, и логотип не должен просвечивать сквозь неё.
    <div className={`rounded-2xl bg-deep ${className}`}>
      <div
        role="status"
        className="flex flex-col items-center gap-1 rounded-2xl bg-amber-400/10 px-5 py-2 text-center text-xs text-amber-300 outline-1 -outline-offset-1 outline-amber-300/20"
      >
        <span>{notice.title}</span>
        <span>{notice.detail}</span>
        <button
          type="button"
          onClick={dismissEngineNotice}
          className="mt-0.5 cursor-pointer whitespace-nowrap rounded-full bg-amber-300/15 px-3 py-px transition-colors hover:bg-amber-300/30 active:bg-amber-300/40 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-amber-300"
        >
          OK
        </button>
      </div>
    </div>
  )
}

// === выход из партии насовсем ================================================

/**
 * Кнопка «Leave game» с подтверждением во всплывающей подсказке под ней: стрелка к кнопке и две
 * кнопки внутри, без текста, — «Exit game» и «Stay». Выход насовсем стирает токен (R111), поэтому одного нажатия мало.
 * Подсказка закрывается щелчком мимо, Escape и потерей связи. `align`: слева от кнопки (в углу поля)
 * или по центру (кнопка на всю ширину карточки в лобби).
 */
export function LeaveGameButton({
  offline,
  onLeave,
  align = 'start',
  className = '',
}: {
  offline: boolean
  onLeave: () => void
  align?: 'start' | 'center'
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (offline) setOpen(false)
  }, [offline])
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    window.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', away)
      window.removeEventListener('keydown', key)
    }
  }, [open])

  const centered = align === 'center'
  const edge = 'border-white/15'
  return (
    <div ref={root} className={`relative ${centered ? 'mt-3 w-full' : ''}`}>
      <button
        type="button"
        disabled={offline}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`${BUTTON_OUTLINE} ${centered ? 'w-full' : ''} ${className}`}
      >
        Leave game
      </button>
      {open && (
        <div
          role="alertdialog"
          aria-label="Leave the game for good?"
          className={`absolute top-full z-[17] mt-3 w-max rounded-xl border ${edge} bg-surface px-4 py-3 ${DIALOG_SHADOW} ${centered ? 'left-1/2 -translate-x-1/2' : 'left-0'}`}
        >
          <span
            aria-hidden="true"
            className={`absolute -top-[7px] size-3 rotate-45 border-t border-l ${edge} bg-surface ${centered ? 'left-1/2 -ml-1.5' : 'left-14'}`}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onLeave}
              className="flex-1 cursor-pointer rounded-full px-4 py-1.5 text-xs font-bold tracking-wide whitespace-nowrap text-danger-l uppercase outline-1 -outline-offset-1 outline-danger-l/60 transition-colors hover:bg-red-500/25 hover:text-red-200 hover:outline-red-400 focus-visible:outline-2 focus-visible:outline-red-300"
            >
              Exit game
            </button>
            <button
              type="button"
              autoFocus
              onClick={() => setOpen(false)}
              className="flex-1 cursor-pointer rounded-full px-4 py-1.5 text-xs font-bold tracking-wide whitespace-nowrap text-gray-300 uppercase outline-1 -outline-offset-1 outline-white/20 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-2 focus-visible:outline-indigo-400"
            >
              Stay
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// === окна игрового экрана ====================================================

/**
 * Когда отсчёт вопроса уходит в акцент: последние секунды. У уведомления его
 * нет — оно и так короткое, и красная цифра с первой секунды читалась бы тревогой.
 */
const HURRY_MS = 5_000

// === вопрос и итог ответа ====================================================

/** Буква варианта по его месту в списке: A, B, C. */
const letterOf = (index: number): string => String.fromCharCode(65 + index)

/**
 * Вопрос при повторном попадании на вход болота. Срок задаёт сервер: когда он
 * истекает, вопрос закрывается там же, и клиенту ничего слать не нужно.
 */
export function QuestionDialog({
  question,
  record,
  waiting,
  endsAt,
  resultEndsAt,
  serverNow,
  totalMs,
  resultTotalMs,
  shift,
  rect,
  zoomFrom,
  onRectChange,
  mine,
  who,
  closing = false,
  onAnswer,
  onClose,
}: {
  question: PublicQuestion | null
  record: Snapshot['recentTurns'][number] | null
  waiting: boolean
  endsAt: number | null
  /** Когда сервер сам закроет итог (его часы); игрок с ходом может закрыть раньше. */
  resultEndsAt: number | null
  serverNow: number
  totalMs: number
  resultTotalMs: number
  shift: number
  rect: DialogRect | null
  zoomFrom: DialogPoint | null
  onRectChange: (rect: DialogRect) => void
  /** Диалог видят все, но отвечает и закрывает его только игрок с ходом. */
  mine: boolean
  /** Имя игрока с ходом: так диалог говорит о нём у остальных. */
  who: string
  closing?: boolean
  onAnswer: (answerId: string) => void
  onClose: () => void
}) {
  const left = useCountdown(question ? endsAt : null, question ? serverNow : null)
  const resultKey = record ? `${record.turn}-${record.at}` : null
  const closeButton = useRef<HTMLButtonElement>(null)
  const closed = useRef(false)
  const close = () => {
    if (!mine || closed.current) return
    closed.current = true
    onClose()
  }
  useEffect(() => {
    closed.current = false
    if (resultKey) closeButton.current?.focus({ preventScroll: true })
  }, [resultKey])
  // Срок итога задаёт сервер: по нему он сам закрывает окно, клиент лишь показывает остаток.
  const resultLeft = useCountdown(resultEndsAt, serverNow)

  /*
   * Клавиатура игрока с ходом: буква ответа (A, B, C — любой регистр) отвечает сразу, стрелки
   * переносят фокус по вариантам по кругу, а Пробел и Enter нажимают тот, что в фокусе, — это уже
   * делает сама кнопка. Остальные игроки диалог лишь видят и ничего не нажимают.
   */
  const choices = useRef<HTMLUListElement>(null)
  const sent = useRef(false)
  // Новый вопрос или новый итог — ответ снова можно отправить.
  const questionText = question?.prompt
  const recordKey = record ? `${record.turn}-${record.at}` : null
  useEffect(() => {
    sent.current = false
  }, [questionText, recordKey])
  const answer = (id: string) => {
    if (sent.current) return
    sent.current = true
    onAnswer(id)
  }
  const answering = !!question && !record && mine && !waiting && !closing
  useEffect(() => {
    if (!answering || !question) return
    const answers = question.answers
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return
      const index = e.key.length === 1 ? e.key.toUpperCase().charCodeAt(0) - 65 : -1
      if (index >= 0 && index < answers.length) {
        e.preventDefault()
        answer(answers[index].id)
        return
      }
      const step =
        e.key === 'ArrowDown' || e.key === 'ArrowRight'
          ? 1
          : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
            ? -1
            : 0
      if (!step) return
      const buttons = [
        ...(choices.current?.querySelectorAll('button:not(:disabled)') ?? []),
      ] as HTMLButtonElement[]
      if (!buttons.length) return
      e.preventDefault()
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
      // Пока фокус не на вариантах, вниз — к первому, вверх — к последнему.
      const next =
        at < 0 ? (step > 0 ? 0 : buttons.length - 1) : (at + step + buttons.length) % buttons.length
      buttons[next].focus({ preventScroll: true })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // `answer` читает только ref и пропс-обработчик: перевешивать слушатель на каждый его вид незачем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answering, question])

  const correct = record?.event === TurnEvent.ANSWER_WIN
  const timedOut = record?.event === TurnEvent.ANSWER_TIMEOUT
  // Тот же итог у всех: игроку с ходом — «You», остальным — по имени.
  const you = mine
  const subject = you ? 'You' : who
  const consequence = record
    ? record.trapId
      ? correct
        ? `${subject} ${you ? 'stay' : 'stays'} at the swamp entrance.`
        : `${subject} ${you ? 'fall' : 'falls'} back to the bottom of the swamp.`
      : record.landed === CellType.LUCK
        ? correct
          ? `${subject} ${you ? 'move' : 'moves'} forward.`
          : `${subject} ${you ? 'stay' : 'stays'} on this cell.`
        : correct
          ? `${subject} ${you ? 'stay' : 'stays'} on this cell.`
          : `${subject} ${you ? 'are' : 'is'} thrown back.`
    : null

  return (
    <Shell
      shift={shift}
      titleId="question-title"
      focusRef={record && mine ? closeButton : undefined}
      onDismiss={record && mine ? close : undefined}
      rect={rect}
      onRectChange={onRectChange}
      zoomFrom={zoomFrom}
      closing={closing}
    >
      {record ? (
        <>
          <div
            className={`mx-auto mb-3 grid size-16 place-items-center rounded-full ${correct ? 'bg-emerald-400/10 text-emerald-400' : 'bg-red-400/10 text-red-400'}`}
          >
            {correct ? (
              <CheckCircleIcon aria-hidden="true" className="size-14" />
            ) : (
              <XCircleIcon aria-hidden="true" className="size-14" />
            )}
          </div>
          {mine && <Timer left={resultLeft} total={resultTotalMs} />}
          <h2 id="question-title" className={`${TITLE_WITH_TIMER} !mb-2`}>
            {timedOut ? "Time's up" : correct ? 'Correct answer' : 'Incorrect answer'}
          </h2>
          {consequence && <p className="mb-2 text-center text-base/6 text-white">{consequence}</p>}
          {record.explanation && (
            <p className="mb-5 text-center text-sm/5 text-gray-400">{record.explanation}</p>
          )}
          {mine && (
            <button ref={closeButton} type="button" onClick={close} className={BUTTON}>
              Close
            </button>
          )}
        </>
      ) : question ? (
        <>
          {mine && <Timer left={left} total={totalMs} hurryMs={HURRY_MS} />}
          <h2 id="question-title" className={mine ? TITLE_WITH_TIMER : CARD_TITLE}>
            Question
          </h2>
          <p className="mb-6 text-base/6 text-white">{question.prompt}</p>
          <ul ref={choices} className="space-y-2" aria-label="Answers">
            {question.answers.map((a, i) => (
              <li key={a.id}>
                <button
                  type="button"
                  disabled={waiting || !mine}
                  onClick={() => answer(a.id)}
                  aria-keyshortcuts={letterOf(i)}
                  className={`${CHOICE} ${mine ? 'disabled:cursor-wait' : 'disabled:cursor-default'} disabled:opacity-60`}
                >
                  <span
                    aria-hidden="true"
                    className="mr-2.5 shrink-0 font-semibold text-gray-400 group-hover:text-white"
                  >
                    {letterOf(i)}.
                  </span>
                  <span>{a.text}</span>
                </button>
              </li>
            ))}
          </ul>
          {/* Строка занята всегда: её появление растило окно сверх зафиксированной высоты. */}
          <p role="status" className="mt-4 h-5 text-center text-sm text-gray-400">
            {waiting ? 'Checking answer…' : mine ? '' : `${who} is answering…`}
          </p>
        </>
      ) : null}
    </Shell>
  )
}

// === падение в колодец =======================================================

/**
 * Уведомление о падении на дно: что за ловушка и почему упал. Одна кнопка и отсчёт до срока сервера: по
 * нему сервер сам передаёт очередь дальше, клиент ничего не подтверждает. Показывается после анимации
 * падения, поэтому остаток может быть меньше окна.
 */
export function FallDialog({
  title,
  lead,
  body,
  shift,
  zoomFrom,
  mine,
  closing = false,
  endsAt,
  serverNow,
  totalMs,
  onAck,
}: {
  title: string
  lead: string
  body: string | null
  /** Когда сервер передаст очередь дальше (его часы). */
  endsAt: number | null
  serverNow: number
  totalMs: number
  shift: number
  zoomFrom?: DialogPoint | null
  /** Подтверждает только игрок с ходом; остальные видят то же окно без кнопки и отсчёта. */
  mine: boolean
  closing?: boolean
  onAck: () => void
}) {
  const left = useCountdown(endsAt, serverNow)
  const button = useRef<HTMLButtonElement>(null)

  // Один раз: после подтверждения диалог уходит, но второй щелчок успел бы уйти ещё.
  const sent = useRef(false)
  const ack = () => {
    if (!mine || sent.current) return
    sent.current = true
    onAck()
  }

  return (
    <Shell
      shift={shift}
      titleId="fall-title"
      focusRef={mine ? button : undefined}
      zoomFrom={zoomFrom}
      closing={closing}
    >
      {mine && <Timer left={left} total={totalMs} />}
      <h2 id="fall-title" className={TITLE_WITH_TIMER}>
        {title}
      </h2>
      <p className="mb-6 text-center text-sm text-gray-400">{lead}</p>
      {body && <p className="mb-6 text-base/6 text-white">{body}</p>}
      {mine && (
        <button ref={button} type="button" onClick={ack} className={BUTTON}>
          OK
        </button>
      )}
    </Shell>
  )
}

// === ожидание старта =========================================================

/**
 * Порог автозапуска: «Off» и числа игроков от двух до максимума. Радио-группа,
 * а не выпадающий список: вариантов мало, и все они должны быть на виду. Выбор
 * действует сразу — это настройка комнаты, а не поле формы.
 */
function AutoStartPicker({
  value,
  max,
  onChange,
}: {
  value: number
  max: number
  onChange: (min: number) => void
}) {
  const options = [0, ...Array.from({ length: Math.max(0, max - 1) }, (_, i) => i + 2)]
  return (
    <div className="flex w-full items-center justify-between gap-4">
      <span id="auto-start-label" className="text-sm text-gray-300">
        Auto-start
      </span>
      <div
        role="radiogroup"
        aria-labelledby="auto-start-label"
        className="flex rounded-full bg-white/5 p-0.5 outline-1 -outline-offset-1 outline-white/10"
      >
        {options.map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={n === 0 ? 'Off' : `At ${n} players`}
            onClick={() => onChange(n)}
            className={`min-w-9 cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400 ${value === n ? 'bg-indigo-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}
          >
            {n === 0 ? 'Off' : n}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Ожидание старта: комната, пока партия не началась (R115, R116).
 *
 * Показывает состав стола, код для приглашения и отсчёт до автозапуска.
 * Хозяин стартует сразу или переключает автозапуск; остальные могут выйти.
 *
 * Решает сервер: отсчёт и сам старт — его. Здесь они только видны,
 * а нажатия уходят командами.
 */
export function WaitingDialog({
  snapshot,
  playerId,
  role,
  shift,
  onStart,
  onAutoStart,
  onLeave,
}: {
  snapshot: Snapshot
  playerId: string | null
  role: Role | null
  shift: number
  onStart: () => void
  onAutoStart: (min: number) => void
  onLeave: () => void
}) {
  const { players, startsAt, serverNow, autoStartMin, settings } = snapshot
  const me = players.find((p) => p.id === playerId)
  const isHost = !!me?.isHost
  const left = useCountdown(startsAt, serverNow)

  const [copied, setCopied] = useState(false)
  const copy = () => {
    try {
      void navigator.clipboard.writeText(snapshot.code).then(
        () => setCopied(true),
        () => undefined,
      )
    } catch {
      // Буфер обмена недоступен (не защищённый контекст, запрет): код виден и так.
    }
  }
  useEffect(() => {
    if (!copied) return
    const id = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(id)
  }, [copied])

  /** Что говорить, пока отсчёта нет: причина, по которой его нет. */
  const hint =
    autoStartMin === 0
      ? 'Auto-start is off. The host starts the game.'
      : `Starts automatically at ${autoStartMin} players.`

  const slots = Math.max(settings.maxPlayers, players.length)
  const { ref: seatsRef, size: seatW } = useSeatSize(slots)

  return (
    <Shell shift={shift} titleId="waiting-title" joinCard>
      {startsAt !== null && <Timer left={left} total={settings.autoStartMs} />}
      <h2 id="waiting-title" className={`${TITLE_WITH_TIMER} truncate`}>
        {snapshot.roomName}
      </h2>

      {/*
        Дальше раскладка карточки входа (`lobby.tsx`, «Join the room») строка в строку:
        сведения о комнате, черта, ряд мест, строка настройки, кнопки. Окно ожидания —
        та же карточка, в которую вошли, и размер у неё не должен меняться.
      */}
      <div className="text-center">
        <div className="flex h-7 items-center justify-center gap-3 text-sm text-gray-400">
          <span>Room code</span>
          <span className="font-mono text-base tracking-widest text-white">{snapshot.code}</span>
          <button
            type="button"
            onClick={copy}
            className="cursor-pointer rounded-full px-2.5 py-1 text-xs text-gray-400 outline-1 -outline-offset-1 outline-white/20 hover:bg-white/5 hover:text-white focus-visible:outline-2 focus-visible:outline-indigo-400"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p role="status" className="mt-3 text-sm text-gray-400">
          {startsAt !== null ? 'Game starts automatically when the timer runs out.' : hint}
        </p>
      </div>

      <hr className="my-6 border-white/10" />

      {/* Места: занятые — фишкой, свободные — пунктирным силуэтом, как в лобби. */}
      <ul
        ref={seatsRef}
        className="flex flex-wrap content-center justify-center gap-x-1 gap-y-3"
        style={{ minHeight: seatHeight(seatW) }}
        aria-label="Players"
      >
        {Array.from({ length: slots }, (_, i) => {
          const p = players[i]
          if (!p)
            return (
              <li key={`free-${i}`}>
                <FreeSeat size={seatW} />
              </li>
            )
          return (
            <li key={p.id} className={`relative ${p.connected ? '' : 'opacity-40'}`}>
              <SeatPlayer {...p} seat={i} size={seatW} />
              {/* Пометка под фишкой не занимает места в ряду: высота ряда та же, что у входа. */}
              {(p.isHost || !p.connected) && (
                <span className="absolute inset-x-0 -bottom-4 text-center text-[11px] text-gray-500">
                  {[!p.connected && 'offline', p.isHost && 'host'].filter(Boolean).join(' · ')}
                </span>
              )}
            </li>
          )
        })}
      </ul>

      <div className="mt-6 flex h-9 items-center">
        {isHost ? (
          <AutoStartPicker value={autoStartMin} max={settings.maxPlayers} onChange={onAutoStart} />
        ) : (
          <div className="flex w-full items-center justify-between gap-4 text-sm">
            <span className="text-gray-300">Auto-start</span>
            <span className="text-gray-400">{autoStartMin === 0 ? 'Off' : `At ${autoStartMin} players`}</span>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-2 min-[26rem]:gap-3">
        <button
          type="button"
          onClick={onLeave}
          className={`${BUTTON_OUTLINE} ${isHost ? '' : 'min-w-0 flex-1'}`}
        >
          {role === 'viewer' ? 'Back to lobby' : 'Leave'}
        </button>
        {isHost && (
          <button type="button" onClick={onStart} className={`${BUTTON} min-w-0 flex-1`}>
            Start now
          </button>
        )}
      </div>
    </Shell>
  )
}

// === победа ==================================================================

/**
 * Итоги партии: кто остановился на финальной клетке. Появляется после того, как
 * фишка доехала, — как и уведомление о падении, чтобы не закрывать то, о чём
 * рассказывает. Таблицу мест считает сервер (`Snapshot.standings`).
 *
 * Отсчёта нет: партия кончилась, торопить некого. «Close» закрывает
 * окно и оставляет поле как есть, «Back to rooms» выходит из комнаты.
 */
export function WinnerDialog({
  snapshot,
  playerId,
  shift,
  onClose,
  onLeave,
}: {
  snapshot: Snapshot
  playerId: string | null
  shift: number
  onClose: () => void
  onLeave: () => void
}) {
  const button = useRef<HTMLButtonElement>(null)
  const places = snapshot.standings.flatMap((id) => snapshot.players.find((p) => p.id === id) ?? [])
  const winner = places.find((p) => p.id === snapshot.winnerId) ?? null
  const mine = winner?.id === playerId

  return (
    <Shell shift={shift} titleId="winner-title" focusRef={button} onDismiss={onClose}>
      <h2 id="winner-title" className={CARD_TITLE}>
        {mine ? 'You made it!' : 'Finish!'}
      </h2>
      <p className="mb-6 text-center text-sm text-gray-400">
        {winner ? (
          <>
            <span className="font-semibold text-white">{winner.name}</span> stopped on the final cell
          </>
        ) : (
          'The game is over'
        )}
      </p>
      <ol className="mb-8 space-y-2" aria-label="Standings">
        {places.map((p, i) => (
          <li
            key={p.id}
            className="flex items-center gap-3 rounded-full bg-white/5 py-1.5 pr-4 pl-1.5 outline-1 -outline-offset-1 outline-white/10"
          >
            <span
              className="grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold text-deep"
              style={{ backgroundColor: markerColor(p.pawn) }}
            >
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-white">
              {p.name}
              {p.id === playerId && <span className="text-gray-500"> (you)</span>}
            </span>
            {i === 0 && p.id === snapshot.winnerId && (
              <span className="text-[11px] font-semibold tracking-wide text-accent uppercase">Winner</span>
            )}
          </li>
        ))}
      </ol>
      <div className="flex gap-2 min-[26rem]:gap-3">
        <button type="button" onClick={onClose} className={`${BUTTON_OUTLINE} min-w-0 flex-1`}>
          Close
        </button>
        <button ref={button} type="button" onClick={onLeave} className={`${BUTTON} min-w-0 flex-1`}>
          Back to rooms
        </button>
      </div>
    </Shell>
  )
}
